import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Sun, Moon } from 'lucide-react';
import { Button } from '@/components/Button';
import { useTheme } from '@/components/ThemeProvider';
import { AppLauncher } from '@/components/AppLauncher';
import { AssistantChat } from '@/components/AssistantChat';
import { Logo } from '@/components/Logo';
import { DashboardEngine } from '@/components/workspace/DashboardEngine';
import { UserMenu } from '@/components/UserMenu';
import { InstallPWA } from '@/components/InstallPWA';
import { CheckInOutButton, CheckInOutCard } from '@/components/attendance/CheckInOut';
import { CelebrationsBanner } from '@/components/CelebrationsBanner';
import { useAdminNotificationStore } from '@/store/adminNotificationStore';
import { useBrandingStore } from '@/store/brandingStore';

/** Bell with the unread count of this user's server notifications; opens the notifications page. */
function NotificationsButton() {
  const navigate = useNavigate();
  const { unread, load } = useAdminNotificationStore();
  useEffect(() => { load(); }, [load]);
  const count = unread;
  return (
    <Button variant="ghost" size="icon-sm" className="relative size-9" onClick={() => navigate('/notifications')}
      aria-label={count ? `Notifications, ${count} unread` : 'Notifications'}>
      <Bell />
      {count > 0 && (
        <span className="absolute right-1 top-1 flex min-w-4 items-center justify-center rounded-full bg-primary px-1 text-xs font-medium leading-4 text-primary-foreground">
          {count > 9 ? '9+' : count}
        </span>
      )}
    </Button>
  );
}

export function Dashboard() {
  const appName = useBrandingStore((s) => s.appName) || 'TeamOne';
  const { theme, toggleTheme } = useTheme();
  return (
    <div className="flex h-screen min-h-screen flex-col overflow-hidden bg-background">
      <header className="z-40 flex h-14 shrink-0 items-center justify-between gap-2 border-b border-border bg-card px-3 sm:px-4 md:px-6">
        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          <AppLauncher />
          <div className="flex items-center gap-2 border-l border-border pl-2 sm:pl-3">
            <Logo showText={false} className="size-7 shrink-0" />
            <span className="hidden font-display text-menu text-foreground sm:inline">{appName}</span>
          </div>
        </div>

        <div className="flex min-w-0 flex-1 items-center justify-end gap-1 sm:gap-2">
          <div className="hidden md:block">
            <InstallPWA />
          </div>
          <CheckInOutButton />
          <Button
            variant="ghost"
            size="icon-sm"
            className="hidden size-9 text-muted-foreground hover:text-foreground sm:inline-flex"
            onClick={toggleTheme}
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <Sun /> : <Moon />}
          </Button>
          <NotificationsButton />
          <div className="ml-0.5 border-l border-border pl-1 sm:ml-1 sm:pl-2">
            <UserMenu />
          </div>
        </div>
      </header>

      <DashboardEngine
        topSlot={
          <>
            <CelebrationsBanner bare />
            <div className="pt-3">
              <CheckInOutCard />
            </div>
          </>
        }
      />
      <AssistantChat />
    </div>
  );
}
